import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../contexts/AuthContext";
import { GraduationCap, Sparkles, ArrowRight, Crown, Loader, ArrowLeft, Home, CheckCircle, Clock, CreditCard, Users, DollarSign, TrendingUp, AlertCircle, FileText, Settings, BarChart3, Shield, Mail, Phone, Calendar, Activity, Package, Zap, Brain, Menu, X, ChevronRight, LayoutDashboard } from "lucide-react";
import { collection, getDocs, query, where, onSnapshot, orderBy, limit } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Helmet } from "react-helmet-async";

interface SystemStats {
  users: number;
  items: number;
  revenue: number;
}

interface RecentActivity {
  id: string;
  type: 'payment' | 'user' | 'course' | 'salepage';
  title: string;
  description: string;
  timestamp: Date;
  status: 'pending' | 'approved' | 'completed';
}

interface QuickStats {
  totalRevenue: number;
  monthlyRevenue: number;
  activeUsers: number;
  pendingPayments: number;
  pendingTrials: number;
  totalCourses: number;
  totalSalepages: number;
}

export default function AdminSelector() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [courseStats, setCourseStats] = useState<SystemStats>({ users: 0, items: 0, revenue: 0 });
  const [salepageStats, setSalepageStats] = useState<SystemStats>({ users: 0, items: 0, revenue: 0 });
  const [aiStats, setAiStats] = useState<SystemStats>({ users: 0, items: 0, revenue: 0 });
  const [totalStats, setTotalStats] = useState({
    totalUsers: 0,
    totalProducts: 0,
    totalRevenue: 0,
    growthRate: 0
  });
  const [pendingApprovals, setPendingApprovals] = useState({
    pendingTrials: 0,
    pendingPayments: 0
  });
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [quickStats, setQuickStats] = useState<QuickStats>({
    totalRevenue: 0,
    monthlyRevenue: 0,
    activeUsers: 0,
    pendingPayments: 0,
    pendingTrials: 0,
    totalCourses: 0,
    totalSalepages: 0
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  useEffect(() => {
    const onResize = () => { const n = window.innerWidth < 900; setNarrow(n); if (!n) setSidebarOpen(false); };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const comingSoon = () => { setToastMsg('ฟีเจอร์นี้อยู่ระหว่างพัฒนา เร็วๆนี้'); setTimeout(() => setToastMsg(null), 2200); };

  useEffect(() => {
    // Check if admin is authenticated via AdminPasswordGuard
    const adminAuthData = sessionStorage.getItem('admin_authenticated');
    if (adminAuthData) {
      try {
        const { timestamp } = JSON.parse(adminAuthData);
        const now = Date.now();
        const SESSION_DURATION = 3600000; // 1 hour in milliseconds
        
        // Check if admin session is still valid
        if (now - timestamp < SESSION_DURATION) {
          loadAllStats();
          return;
        }
      } catch (e) {
        // Invalid session data, continue to check Firebase user
      }
    }

    // Check if Firebase user is admin (fallback)
    if (user?.email !== "viitamint@gmail.com" && user?.role !== "admin") {
      navigate("/");
      return;
    }

    loadAllStats();
  }, [user, navigate]);

  // Real-time listener for pending approvals
  useEffect(() => {
    if (!user) return;

    // Listen to trial_pending users
    const trialsQuery = query(
      collection(db, "users"),
      where("salePageSubscription.status", "==", "trial_pending")
    );
    
    const unsubscribeTrials = onSnapshot(trialsQuery, (snapshot) => {
      setPendingApprovals(prev => ({
        ...prev,
        pendingTrials: snapshot.size
      }));
    });

    // Listen to pending payments
    const paymentsQuery = query(
      collection(db, "salePagePayments"),
      where("status", "==", "pending")
    );
    
    const unsubscribePayments = onSnapshot(paymentsQuery, (snapshot) => {
      setPendingApprovals(prev => ({
        ...prev,
        pendingPayments: snapshot.size
      }));
    });

    return () => {
      unsubscribeTrials();
      unsubscribePayments();
    };
  }, [user]);

  const loadAllStats = async () => {
    try {
      setLoading(true);

      // Load Course System Stats
      const courseUsersSnap = await getDocs(
        query(collection(db, "users"), where("role", "==", "instructor"))
      );
      const courseUsers = courseUsersSnap.size;

      const coursesSnap = await getDocs(collection(db, "courses"));
      const totalCourses = coursesSnap.size;

      const coursePaymentsSnap = await getDocs(
        query(collection(db, "subscription_payments"), where("status", "==", "approved"))
      );
      const courseRevenue = coursePaymentsSnap.docs.reduce(
        (sum, doc) => sum + (Number(doc.data().amount) || 0),
        0
      );

      setCourseStats({
        users: courseUsers,
        items: totalCourses,
        revenue: courseRevenue
      });

      // Load Salepage System Stats
      // Salepage subscriptions live in the salePageSubscriptions collection (source of truth),
      // not the users.salePageSubscription nested field (which is unpopulated → previously counted 0).
      const salepageSubsSnap = await getDocs(collection(db, "salePageSubscriptions"));
      const salepageUsers = salepageSubsSnap.docs.filter(d =>
        ['trial', 'active', 'trial_pending'].includes(d.data().status)
      ).length;

      const salepagesSnap = await getDocs(collection(db, "salepages"));
      const totalSalepages = salepagesSnap.size;

      const salepagePaymentsSnap = await getDocs(
        query(collection(db, "salePagePayments"), where("status", "==", "verified"))
      );
      const salepageRevenue = salepagePaymentsSnap.docs.reduce(
        (sum, doc) => sum + (Number(doc.data().amount) || 0),
        0
      );

      setSalepageStats({
        users: salepageUsers,
        items: totalSalepages,
        revenue: salepageRevenue
      });

      // Load AI System Stats
      const aiPackagesSnap = await getDocs(collection(db, 'aiPackages'));
      const totalAIPackages = aiPackagesSnap.size;

      // Count only PAID users from coursedee_users collections
      const coursedeeV1Snap = await getDocs(collection(db, 'coursedee_v1_users'));
      const coursedeeV2Snap = await getDocs(collection(db, 'coursedee_v2_users'));
      
      const paidV1Users = coursedeeV1Snap.docs.filter(doc => doc.data().isPaid === true);
      const paidV2Users = coursedeeV2Snap.docs.filter(doc => doc.data().isPaid === true);
      const aiUsers = paidV1Users.length + paidV2Users.length;

      // Calculate AI revenue from actual paid users
      let aiRevenue = 0;
      paidV1Users.forEach(doc => {
        const data = doc.data();
        if (data.amount) {
          aiRevenue += Number(data.amount);
        }
      });
      paidV2Users.forEach(doc => {
        const data = doc.data();
        if (data.amount) {
          aiRevenue += Number(data.amount);
        }
      });

      setAiStats({
        users: aiUsers,
        items: totalAIPackages,
        revenue: aiRevenue
      });

      // Calculate Total Stats
      const totalUsers = courseUsers + salepageUsers;
      const totalProducts = totalCourses + totalSalepages;
      const totalRevenue = courseRevenue + salepageRevenue;

      // Calculate growth rate (compare this month vs last month)
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

      const thisMonthRevenue = [...coursePaymentsSnap.docs, ...salepagePaymentsSnap.docs]
        .filter(doc => {
          const createdAt = doc.data().createdAt?.toDate();
          return createdAt && createdAt >= thisMonthStart;
        })
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      const lastMonthRevenue = [...coursePaymentsSnap.docs, ...salepagePaymentsSnap.docs]
        .filter(doc => {
          const createdAt = doc.data().createdAt?.toDate();
          return createdAt && createdAt >= lastMonthStart && createdAt <= lastMonthEnd;
        })
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      const growthRate = lastMonthRevenue > 0
        ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100
        : 0;

      setTotalStats({
        totalUsers,
        totalProducts,
        totalRevenue,
        growthRate
      });

      // Load Quick Stats
      const allUsersSnap = await getDocs(collection(db, "users"));
      const activeUsers = allUsersSnap.filter(doc => {
        const data = doc.data();
        return (data.subscriptionTier && data.subscriptionTier !== 'free') ||
               (data.salePageSubscription?.status === 'active');
      }).length;

      const pendingPaymentsQuery = query(
        collection(db, "salePagePayments"),
        where("status", "==", "pending")
      );
      const pendingPaymentsSnap = await getDocs(pendingPaymentsQuery);

      const pendingTrialsQuery = query(
        collection(db, "users"),
        where("salePageSubscription.status", "==", "trial_pending")
      );
      const pendingTrialsSnap = await getDocs(pendingTrialsQuery);

      setQuickStats({
        totalRevenue,
        monthlyRevenue: thisMonthRevenue,
        activeUsers,
        pendingPayments: pendingPaymentsSnap.size,
        pendingTrials: pendingTrialsSnap.size,
        totalCourses,
        totalSalepages
      });

      // Load Recent Activities
      const activities: RecentActivity[] = [];

      // Recent payments
      const recentPaymentsQuery = query(
        collection(db, "salePagePayments"),
        orderBy("createdAt", "desc"),
        limit(5)
      );
      const recentPaymentsSnap = await getDocs(recentPaymentsQuery);
      recentPaymentsSnap.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: doc.id,
          type: 'payment',
          title: `การชำระเงินจาก ${data.email}`,
          description: `จำนวน ${data.amount} บาท`,
          timestamp: data.createdAt?.toDate() || new Date(),
          status: data.status === 'verified' ? 'approved' : data.status
        });
      });

      // Recent trial requests
      const recentTrialsQuery = query(
        collection(db, "users"),
        where("salePageSubscription.status", "==", "trial_pending"),
        orderBy("salePageSubscription.trialRequestedAt", "desc"),
        limit(3)
      );
      const recentTrialsSnap = await getDocs(recentTrialsQuery);
      recentTrialsSnap.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: doc.id,
          type: 'user',
          title: `คำขอทดลองใช้ฟรีจาก ${data.email}`,
          description: 'รอการอนุมัติ',
          timestamp: data.salePageSubscription?.trialRequestedAt?.toDate() || new Date(),
          status: 'pending'
        });
      });

      // Sort activities by timestamp
      activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setRecentActivities(activities.slice(0, 8));

      setLoading(false);
    } catch (error) {
      console.error("Error loading stats:", error);
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) {
      return 'เมื่อสักครู่';
    } else if (hours < 24) {
      return `${hours} ชั่วโมงที่แล้ว`;
    } else if (days < 7) {
      return `${days} วันที่แล้ว`;
    } else {
      return new Intl.DateTimeFormat('th-TH', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).format(date);
    }
  };

  const systems = [
    {
      id: "course",
      title: "Course System",
      subtitle: "ระบบสร้างคอร์สเรียน",
      description: "จัดการผู้ใช้งาน คอร์สเรียน การชำระเงิน และ Analytics",
      icon: GraduationCap,
      gradient: "from-indigo-500 via-purple-500 to-pink-500",
      path: "/admin/course-system",
      stats: [
        { label: "Users", value: loading ? "..." : courseStats.users.toString() },
        { label: "Courses", value: loading ? "..." : courseStats.items.toString() },
        { label: "Revenue", value: loading ? "..." : formatCurrency(courseStats.revenue) }
      ]
    },
    {
      id: "salepage",
      title: "Salepage System",
      subtitle: "ระบบสร้างหน้าขาย",
      description: "จัดการผู้ใช้งาน Salepage การชำระเงิน และ Analytics",
      icon: Sparkles,
      gradient: "from-emerald-500 via-teal-500 to-cyan-500",
      path: "/admin/salepage-system",
      stats: [
        { label: "Users", value: loading ? "..." : salepageStats.users.toString() },
        { label: "Pages", value: loading ? "..." : salepageStats.items.toString() },
        { label: "Revenue", value: loading ? "..." : formatCurrency(salepageStats.revenue) }
      ]
    },
    {
      id: "ai",
      title: "AI System",
      subtitle: "ระบบจัดการ AI",
      description: "จัดการคอร์ส AI การชำระเงิน และการวิเคราะห์ข้อมูล",
      icon: Brain,
      gradient: "from-purple-500 via-violet-500 to-indigo-500",
      path: "/admin/ai-system",
      stats: [
        { label: "AI Courses", value: loading ? "..." : aiStats.items.toString() },
        { label: "Users", value: loading ? "..." : aiStats.users.toString() },
        { label: "Revenue", value: loading ? "..." : formatCurrency(aiStats.revenue) }
      ]
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>
      <div style={{ minHeight: '100vh', display: 'flex', background: '#F3F8F8', color: '#0E3A40', fontFamily: '"IBM Plex Sans Thai", system-ui, sans-serif' }}>
        {(!narrow || sidebarOpen) && (
          <aside style={{ ...(narrow ? { position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 50, boxShadow: '0 0 40px rgba(0,0,0,0.25)', transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform .25s' } : { position: 'sticky', top: 0, height: '100vh' }), width: 240, flex: '0 0 auto', background: '#0E3A40', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '22px 20px 20px' }}>
              <span style={{ width: 36, height: 36, borderRadius: 11, background: 'linear-gradient(135deg, #00B4A1, #3A93B9)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}><Crown style={{ width: 17, height: 17, color: '#fff' }} /></span>
              <div>
                <div style={{ fontFamily: "'Anuphan',sans-serif", fontWeight: 700, fontSize: 16.5, color: '#fff' }}>Admin</div>
                <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.55)' }}>EduMi Platform</div>
              </div>
              {narrow && (
                <button type="button" onClick={() => setSidebarOpen(false)} style={{ marginLeft: 'auto', width: 30, height: 30, borderRadius: 9, border: 0, background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X style={{ width: 15, height: 15 }} /></button>
              )}
            </div>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 12px' }}>
              {[
                { icon: LayoutDashboard, label: 'ภาพรวม', active: true, go: null },
                { icon: CreditCard, label: 'การชำระเงิน', go: '/admin/payment-approval' },
                { icon: Users, label: 'จัดการสมาชิก', go: '/admin/users' },
                { icon: FileText, label: 'สมัครสมาชิก', go: '/admin/subscription-payments' },
                { icon: BarChart3, label: 'สถิติและรายงาน', go: '/admin/analytics' },
                { icon: Settings, label: 'ตั้งค่าระบบ', go: '/admin/settings' },
              ].map((it) => {
                const Ic = it.icon;
                return (
                  <button key={it.label} type="button" onClick={() => { if (it.active) return; if (it.go) { setSidebarOpen(false); navigate(it.go); } else { comingSoon(); } }} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 11, fontSize: 14, fontWeight: 500, border: 0, cursor: 'pointer', textAlign: 'left', fontFamily: '"IBM Plex Sans Thai", sans-serif', background: it.active ? 'linear-gradient(120deg, #00B4A1, #3A93B9)' : 'transparent', color: it.active ? '#fff' : 'rgba(255,255,255,0.72)' }}>
                    <Ic style={{ width: 17, height: 17 }} /> {it.label}
                    {!it.active && !it.go && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 999, background: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.65)' }}>เร็วๆนี้</span>}
                  </button>
                );
              })}
            </nav>
            <button type="button" onClick={() => navigate('/')} style={{ margin: 'auto 12px 18px', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', borderRadius: 11, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', fontSize: 13.5, border: 0, cursor: 'pointer', fontFamily: '"IBM Plex Sans Thai", sans-serif' }}><Home style={{ width: 16, height: 16 }} /> กลับหน้าแรก</button>
          </aside>
        )}
        {narrow && sidebarOpen && <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(14,58,64,0.4)', zIndex: 40 }} />}

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <header style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(243,248,248,0.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(14,58,64,0.08)' }}>
            <div style={{ padding: '16px clamp(16px,3vw,32px)', display: 'flex', alignItems: 'center', gap: 14 }}>
              {narrow && (
                <button type="button" onClick={() => setSidebarOpen(true)} style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid rgba(14,58,64,0.12)', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', cursor: 'pointer' }}><Menu style={{ width: 17, height: 17, color: '#0E3A40' }} /></button>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ fontFamily: "'Anuphan',sans-serif", fontWeight: 700, fontSize: 'clamp(19px,2.2vw,23px)', margin: 0, letterSpacing: '-0.02em' }}>Admin Dashboard</h1>
                <p style={{ fontSize: 13, color: '#5B7D80', margin: '2px 0 0' }}>ศูนย์รวมการจัดการระบบ EduMi</p>
              </div>
            </div>
          </header>

          <main style={{ flex: 1, padding: 'clamp(20px,3vw,28px) clamp(16px,3vw,32px) clamp(48px,6vw,80px)', display: 'flex', flexDirection: 'column', gap: 'clamp(22px,2.8vw,28px)' }}>
            <div style={{ background: '#0E3A40', backgroundImage: 'radial-gradient(90% 120% at 100% -10%, rgba(0,180,161,0.5), transparent 60%), radial-gradient(80% 100% at 0% 110%, rgba(91,111,203,0.4), transparent 60%)', borderRadius: 20, padding: 'clamp(18px,2.2vw,22px) clamp(20px,2.6vw,26px)', display: 'flex', flexWrap: 'wrap', gap: 'clamp(20px,4vw,40px)', color: '#fff' }}>
              <div><div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginBottom: 5 }}>รายได้รวม</div><div style={{ fontFamily: "'Anuphan',sans-serif", fontWeight: 700, fontSize: 24 }}>{formatCurrency(totalStats.totalRevenue)}</div></div>
              <div><div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginBottom: 5 }}>ผู้ใช้งานทั้งหมด</div><div style={{ fontFamily: "'Anuphan',sans-serif", fontWeight: 700, fontSize: 24 }}>{totalStats.totalUsers}</div></div>
              <div><div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginBottom: 5 }}>รายได้เดือนนี้</div><div style={{ fontFamily: "'Anuphan',sans-serif", fontWeight: 700, fontSize: 24 }}>{formatCurrency(quickStats.monthlyRevenue)}</div></div>
              <div><div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginBottom: 5 }}>อัตราเติบโต</div><div style={{ fontFamily: "'Anuphan',sans-serif", fontWeight: 700, fontSize: 24 }}>{(totalStats.growthRate >= 0 ? '+' : '') + totalStats.growthRate.toFixed(1)}%</div></div>
            </div>

            {pendingApprovals.pendingPayments > 0 && (
              <button type="button" onClick={() => navigate('/admin/payment-approval')} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, background: 'rgba(255,122,69,0.08)', border: '1px solid rgba(255,122,69,0.3)', borderRadius: 16, padding: 'clamp(14px,1.8vw,17px) clamp(16px,2vw,20px)', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                <span style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,122,69,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}><AlertCircle style={{ width: 17, height: 17, color: '#FF7A45' }} /></span>
                <span style={{ flex: 1, fontSize: 13.5, color: '#8A4A26' }}><strong style={{ fontFamily: "'Anuphan',sans-serif" }}>{pendingApprovals.pendingPayments} รายการ</strong> รอตรวจสอบการโอนเงิน</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#FF7A45' }}>ตรวจสอบเลย →</span>
              </button>
            )}

            <div style={{ background: '#fff', borderRadius: 20, border: '1px solid rgba(14,58,64,0.06)', overflow: 'hidden' }}>
              <div style={{ padding: 'clamp(16px,2vw,20px) clamp(16px,2vw,20px) 0' }}>
                <span style={{ fontFamily: "'Anuphan',sans-serif", fontWeight: 600, fontSize: 16 }}>ระบบทั้งหมด</span>
              </div>
              {systems.map((s) => {
                const Ic = s.icon;
                const iconBg = s.id === 'course' ? 'linear-gradient(135deg, #5B6FCB, #3A93B9)' : s.id === 'salepage' ? 'linear-gradient(135deg, #00B4A1, #3A93B9)' : 'linear-gradient(135deg, #5B6FCB, #00B4A1)';
                return (
                  <button key={s.id} type="button" onClick={() => navigate(s.path)} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 'clamp(14px,1.8vw,18px) clamp(16px,2vw,20px)', border: 0, borderTop: '1px solid rgba(14,58,64,0.06)', width: '100%', background: 'transparent', cursor: 'pointer', textAlign: 'left', flexWrap: 'wrap', color: 'inherit' }}>
                    <span style={{ width: 44, height: 44, borderRadius: 13, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}><Ic style={{ width: 20, height: 20, color: '#fff' }} /></span>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontFamily: "'Anuphan',sans-serif", fontWeight: 600, fontSize: 15 }}>{s.title}</div>
                      <div style={{ fontSize: 12.5, color: '#5B7D80' }}>{s.subtitle}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 20 }}>
                      {s.stats.map((st, i) => (
                        <div key={i} style={{ textAlign: 'center' }}>
                          <div style={{ fontFamily: "'Anuphan',sans-serif", fontWeight: 700, fontSize: 16 }}>{st.value}</div>
                          <div style={{ fontSize: 11.5, color: '#5B7D80' }}>{st.label}</div>
                        </div>
                      ))}
                    </div>
                    <ChevronRight style={{ width: 17, height: 17, color: '#8AA6A8', flex: '0 0 auto' }} />
                  </button>
                );
              })}
            </div>
          </main>
        </div>

        {toastMsg && <div style={{ position: 'fixed', bottom: 26, left: '50%', transform: 'translateX(-50%)', background: '#0E3A40', color: '#fff', fontSize: 13.5, padding: '12px 20px', borderRadius: 999, boxShadow: '0 20px 40px -18px rgba(14,58,64,0.6)', zIndex: 100 }}>{toastMsg}</div>}
      </div>
    </>
  );
}
